import {
  initButtons,
  initCategoryPreview,
  initDisclosures,
  initExplorer,
  initRails,
  initSessionFilters,
  initTablists
} from "./am-chunk-chunk-6JHQUSBG.js";
import {
  initForms
} from "./am-chunk-chunk-OH2O6BCS.js";
import {
  initReveal
} from "./am-chunk-chunk-VYKTA7OK.js";
import {
  initCart
} from "./am-chunk-chunk-YVZIKS4K.js";
import "./am-chunk-chunk-GIDSMFVY.js";
import "./am-chunk-chunk-D3NAPW5R.js";
import {
  localizeDates
} from "./am-chunk-chunk-OUJFA6OY.js";
import {
  initDialogs
} from "./am-chunk-chunk-INXWTKT7.js";
import {
  clamp,
  debounce,
  finePointer,
  isRTL,
  lerp,
  on,
  parseHTML,
  qs,
  qsa,
  rafLoop,
  reducedMotion,
  theme,
  whenVisible
} from "./am-chunk-chunk-7WWH2AU3.js";

// src/animations/motion-scenes.js
var scenes = /* @__PURE__ */ new Set();
var pointer = { x: 0, y: 0, tx: 0, ty: 0 };
var loop;
function ensureLoop() {
  if (loop) return;
  loop = rafLoop(() => {
    pointer.x = lerp(pointer.x, pointer.tx, 0.08);
    pointer.y = lerp(pointer.y, pointer.ty, 0.08);
    for (const s of scenes) if (s.visible) s.update();
  });
  loop.start();
  if (finePointer()) {
    window.addEventListener(
      "pointermove",
      (e) => {
        pointer.tx = e.clientX / window.innerWidth * 2 - 1;
        pointer.ty = e.clientY / window.innerHeight * 2 - 1;
      },
      { passive: true }
    );
  }
}
function track(scene) {
  scenes.add(scene);
  const io = new IntersectionObserver(([entry]) => scene.visible = entry.isIntersecting, { rootMargin: "100px" });
  io.observe(scene.el);
  ensureLoop();
}
function heroScene(el) {
  const layers = qsa("[data-depth]", el).map((node) => ({
    node,
    depth: parseFloat(node.dataset.depth || "0"),
    sx: parseFloat(node.dataset.scrollX || "0"),
    sy: parseFloat(node.dataset.scrollY || "0"),
    sr: parseFloat(node.dataset.scrollR || "0")
  }));
  const fine = finePointer();
  const rtl = document.documentElement.dir === "rtl" ? -1 : 1;
  return {
    el,
    visible: true,
    update() {
      const rect = el.getBoundingClientRect();
      const progress = clamp(-rect.top / Math.max(1, rect.height), 0, 1);
      for (const l of layers) {
        const px = fine ? pointer.x * l.depth * 22 : 0;
        const py = fine ? pointer.y * l.depth * 16 : 0;
        const x = px + progress * l.sx * window.innerWidth * 0.35 * rtl;
        const y = py + progress * l.sy * window.innerHeight * 0.5 + progress * l.depth * -60;
        const r = progress * l.sr;
        l.node.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) rotate(${r.toFixed(2)}deg)`;
      }
    }
  };
}
function manifestoScene(el) {
  const text = el.querySelector("[data-lit-text]");
  const words = [];
  if (text) {
    const walk = (node) => {
      for (const child of Array.from(node.childNodes)) {
        if (child.nodeType === 3) {
          const frag = document.createDocumentFragment();
          for (const part of child.textContent.split(/(\s+)/)) {
            if (!part) continue;
            if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
            else {
              const w = document.createElement("span");
              w.className = "lit-word";
              w.textContent = part;
              frag.appendChild(w);
              words.push(w);
            }
          }
          child.replaceWith(frag);
        } else if (child.nodeType === 1) walk(child);
      }
    };
    walk(text);
  }
  const stacked = window.matchMedia("(max-width: 749px)").matches;
  return {
    el,
    visible: true,
    update() {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const span = stacked ? rect.height + vh * 0.4 : Math.max(1, rect.height - vh);
      const p = stacked ? clamp((vh * 0.85 - rect.top) / span) : clamp(-rect.top / span);
      el.style.setProperty("--mp", p.toFixed(3));
      const lit = p * 1.15 * words.length;
      for (let i = 0; i < words.length; i++) {
        const v = clamp(lit - i, 0, 1);
        if (words[i]._v !== v) {
          words[i]._v = v;
          words[i].style.setProperty("--p", v.toFixed(2));
        }
      }
    }
  };
}
function parallaxScene(nodes) {
  const items = nodes.map((node) => ({ node, speed: parseFloat(node.dataset.speed || "0.05") }));
  return {
    el: nodes[0].closest("section") || nodes[0],
    visible: true,
    update() {
      const vh = window.innerHeight;
      for (const it of items) {
        const r = it.node.getBoundingClientRect();
        const center = r.top + r.height / 2 - vh / 2;
        it.node.style.transform = `translate3d(0, ${(-center * it.speed).toFixed(2)}px, 0)`;
      }
    }
  };
}
function initScenes(root = document) {
  if (reducedMotion()) {
    return;
  }
  qsa("[data-hero], [data-final-cta]", root).forEach((el) => track(heroScene(el)));
  qsa("[data-manifesto]", root).forEach((el) => track(manifestoScene(el)));
  const speedNodes = qsa("[data-speed]", root);
  if (speedNodes.length && window.innerWidth >= 990) track(parallaxScene(speedNodes));
}

// src/animations/cursor.js
function initCursor() {
  const root = document.querySelector("[data-cursor-root]");
  if (!root || !finePointer() || reducedMotion() || window.theme?.cursor === false) return;
  const label = root.querySelector("[data-cursor-label]");
  const pos = { x: -100, y: -100, tx: -100, ty: -100 };
  let target = null;
  const loop2 = rafLoop(() => {
    pos.x = lerp(pos.x, pos.tx, 0.22);
    pos.y = lerp(pos.y, pos.ty, 0.22);
    root.style.setProperty("--cx", `${pos.x.toFixed(1)}px`);
    root.style.setProperty("--cy", `${pos.y.toFixed(1)}px`);
    if (Math.abs(pos.x - pos.tx) < 0.1 && Math.abs(pos.y - pos.ty) < 0.1) loop2.stop();
  });
  const setTarget = (el) => {
    if (el === target) return;
    target = el;
    if (el) {
      label.textContent = el.dataset.cursor;
      root.dataset.state = el.dataset.cursorState || "view";
      root.classList.add("is-label");
    } else {
      root.classList.remove("is-label");
      root.dataset.state = "";
    }
  };
  document.addEventListener(
    "pointermove",
    (e) => {
      if (e.pointerType !== "mouse") return;
      pos.tx = e.clientX;
      pos.ty = e.clientY;
      root.classList.add("is-visible");
      const over = e.target.closest?.("input, textarea, select, [contenteditable]");
      root.classList.toggle("is-hidden", !!over);
      setTarget(over ? null : e.target.closest?.("[data-cursor]") || null);
      loop2.start();
    },
    { passive: true }
  );
  document.addEventListener("pointerleave", () => root.classList.remove("is-visible"));
  window.addEventListener("blur", () => root.classList.remove("is-visible"));
}

// src/components/header.js
function initHeader() {
  const header = qs("[data-header]");
  if (!header) return;
  let lastY = window.scrollY;
  let ticking = false;
  const onScroll = () => {
    const y = window.scrollY;
    header.classList.toggle("is-scrolled", y > 24);
    const anyMegaOpen = header.querySelector("[data-mega]:not([hidden])");
    if (!anyMegaOpen && !reducedMotion()) {
      header.classList.toggle("is-hidden", y > 420 && y > lastY + 4);
      if (y < lastY - 4) header.classList.remove("is-hidden");
    }
    lastY = y;
    ticking = false;
  };
  window.addEventListener(
    "scroll",
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(onScroll);
      }
    },
    { passive: true }
  );
  onScroll();
  const nav = qs("[data-nav]", header);
  const indicator = qs("[data-nav-indicator]", header);
  const moveIndicator = (link) => {
    if (!nav || !indicator) return;
    if (!link) {
      nav.classList.remove("has-indicator");
      return;
    }
    const navRect = nav.getBoundingClientRect();
    const r = link.getBoundingClientRect();
    const x = isRTL() ? r.left - navRect.left : r.left - navRect.left;
    indicator.style.setProperty("--x", `${x}px`);
    indicator.style.setProperty("--w", `${r.width}px`);
    nav.classList.add("has-indicator");
  };
  if (nav) {
    on(nav, "pointerover", ".site-nav__link", (e, link) => moveIndicator(link));
    on(nav, "focusin", ".site-nav__link", (e, link) => moveIndicator(link));
    nav.addEventListener("pointerleave", () => {
      const open = qs('[aria-expanded="true"]', nav);
      moveIndicator(open);
    });
  }
  const triggers = qsa("[data-mega-trigger]", header);
  let closeTimer;
  const setOpen = (trigger, open) => {
    const panel = document.getElementById(trigger.getAttribute("aria-controls"));
    if (!panel) return;
    trigger.setAttribute("aria-expanded", String(open));
    panel.hidden = !open;
    header.classList.toggle("is-open", triggers.some((t) => t.getAttribute("aria-expanded") === "true"));
    if (open) {
      const top = header.getBoundingClientRect().bottom;
      panel.style.setProperty("--mega-top", `${top}px`);
    }
  };
  const closeAll = (except) => triggers.forEach((t) => t !== except && setOpen(t, false));
  triggers.forEach((trigger) => {
    const item = trigger.closest("[data-nav-item]");
    const panel = document.getElementById(trigger.getAttribute("aria-controls"));
    const fine = window.matchMedia("(hover: hover)").matches;
    if (fine) {
      item.addEventListener("pointerenter", () => {
        clearTimeout(closeTimer);
        closeAll(trigger);
        setOpen(trigger, true);
      });
      item.addEventListener("pointerleave", () => {
        closeTimer = setTimeout(() => setOpen(trigger, false), 180);
      });
    }
    trigger.addEventListener("click", (e) => {
      if (trigger.getAttribute("aria-expanded") !== "true") {
        e.preventDefault();
        closeAll(trigger);
        setOpen(trigger, true);
      } else if (!fine) {
        e.preventDefault();
        setOpen(trigger, false);
      }
    });
    trigger.addEventListener("keydown", (e) => {
      if (e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        setOpen(trigger, true);
        panel.querySelector("a")?.focus();
      }
    });
    panel?.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        setOpen(trigger, false);
        trigger.focus();
      }
    });
  });
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") closeAll();
  });
  document.addEventListener("click", (e) => {
    if (!e.target.closest("[data-nav-item]")) closeAll();
  });
  header.addEventListener("focusout", (e) => {
    if (!header.contains(e.relatedTarget)) closeAll();
  });
  const announce = qs("[data-announce]");
  const msgs = announce ? qsa(".announce__msg", announce) : [];
  if (msgs.length > 1 && !reducedMotion()) {
    let i = 0;
    let paused = false;
    announce.addEventListener("pointerenter", () => paused = true);
    announce.addEventListener("pointerleave", () => paused = false);
    announce.addEventListener("focusin", () => paused = true);
    announce.addEventListener("focusout", () => paused = false);
    setInterval(() => {
      if (paused || document.hidden) return;
      msgs[i].classList.remove("is-active");
      i = (i + 1) % msgs.length;
      msgs[i].classList.add("is-active");
    }, 5e3);
  } else if (msgs.length > 1) {
    msgs.forEach((m) => m.classList.add("is-active"));
    announce.querySelector("[data-announce-track]").style.display = "block";
  }
}

// src/components/search.js
function initSearch() {
  const modal = qs("[data-search-modal]");
  if (!modal) return;
  const input = qs("[data-predictive-input]", modal);
  const results = qs("[data-predictive-results]", modal);
  let controller;
  const run = debounce(async () => {
    const q = input.value.trim();
    if (!q) {
      results.innerHTML = "";
      return;
    }
    controller?.abort();
    controller = new AbortController();
    const base = theme().routes?.predictiveSearch || "/search/suggest";
    const params = new URLSearchParams({
      q,
      section_id: "predictive-search",
      "resources[type]": "product,page,collection",
      "resources[limit]": "6"
    });
    try {
      results.setAttribute("aria-busy", "true");
      const res = await fetch(`${base}?${params}`, { signal: controller.signal });
      const html = await res.text();
      const doc = parseHTML(html);
      const content = doc.querySelector("[data-predictive-content]");
      results.innerHTML = content ? content.outerHTML : "";
    } catch (e) {
      if (e.name !== "AbortError") results.innerHTML = "";
    } finally {
      results.removeAttribute("aria-busy");
    }
  }, 220);
  input?.addEventListener("input", run);
  on(modal, "click", "[data-search-suggestion]", (e, chip) => {
    e.preventDefault();
    input.value = chip.textContent.trim();
    input.focus();
    run();
  });
}

// src/scripts/main.js
window.__amReady = true;
var lazy = [
  ["[data-quiz]", () => import("./am-chunk-quiz-JB4X2RI5.js")],
  ["[data-playground-section]", () => import("./am-chunk-playground-XL5UP45R.js")],
  ["[data-workshop-directory]", () => import("./am-chunk-workshop-directory-PE4IMIXC.js"), true],
  ["[data-booking]", () => import("./am-chunk-booking-ZGN76DW5.js"), true],
  ["[data-free-class]", () => import("./am-chunk-free-class-TZLQKLKQ.js"), true],
  ["[data-product-page]", () => import("./am-chunk-product-ZM24K33I.js"), true],
  ["[data-gallery-page]", () => import("./am-chunk-gallery-3JO6MAY7.js"), true],
  ["[data-collection-filters]", () => import("./am-chunk-collection-filters-XK3POL63.js"), true]
];
function boot() {
  localizeDates();
  initDialogs();
  initHeader();
  initCart();
  initSearch();
  initForms();
  initButtons();
  initRails();
  initCategoryPreview();
  initExplorer();
  initSessionFilters();
  initTablists();
  initDisclosures();
  initReveal();
  initScenes();
  initCursor();
  for (const [selector, loader, eager] of lazy) {
    const els = qsa(selector);
    if (!els.length) continue;
    const run = (el) => loader().then((m) => {
      m.default?.(el);
      el.dataset.amReady = "1";
    }).catch((err) => console.error(`[am] failed to load module for ${selector}`, err));
    if (eager) els.forEach(run);
    else whenVisible(els, run, "400px");
  }
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
